import {
  Box,
  FormControlLabel,
  Switch,
  Tooltip,
  Button as UIButton,
  Alert,
  Snackbar,
} from "@mui/material";
import Addicon from "@mui/icons-material/Add";
import FormatListBulleted from "@mui/icons-material/FormatListBulleted";
import dateformat from "dateformat";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
// Icons
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { Controller, useForm } from "react-hook-form";
import { createUseStyles } from "$utils/createUseStyles";
import { useSnackbar } from "$utils/useSnackbar";
import CalendarCheckFillIcon from "remixicon-react/CalendarEventFillIcon";
import Edit2LineIcon from "remixicon-react/Edit2LineIcon";
import UserLineIcon from "remixicon-react/UserLineIcon";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ExpandMore } from "@mui/icons-material";
import Download2LineIcon from "remixicon-react/Download2LineIcon";
import { theme } from "$utils/theme/bataillog";
import {
  BtnPoper,
  Button,
  InfoContainer,
  Layout,
  Modal,
  Popin,
  Table,
  Text,
} from "$components";
import JTooltip from "$components/JTooltip/JTooltip";
import useAuth from "$hooks/useAuth";
import ProjectForm from "./ProjectForm";
import JImportModal from "$components/JImportModal/JImportModal";
import DockyardForm from "$containers/Dockyard/DockyardForm";
import DockyardIcon from "$containers/Dockyard/DockyardIcon";
import useDockyardForm from "$hooks/useDockyardForm";
import useProjectForm from "$hooks/useProjectForm";
import { DockyardServices, ProjectServices } from "$services";
import { ImportType } from "$types/import/ImportType";
import { ProjectStatus } from "$types/ProjectStatus";
import {
  snackbarErrorOptions,
  snackbarSuccessOptions,
} from "$utils/SnackbarOptions";
import { DEFAULT_DATE_FORMAT } from "$utils/utils";
import ProjectStatusBadge from "./ProjectStatusBadge";
import FileUploader from "$components/FileUploader/FileUploader";
import { IDockyard } from "$services/Dockyard.service";
import { ProjectType } from "$types";
import { CacheInvalidationAction } from "$utils/cache";

const PROJECT_DEACTIVATION_BLOCKED_MESSAGE =
  "Ce projet ne peut pas être désactivé car des EDB ou préparations sont en cours.";

const isProjectDeactivationBlockedError = (message?: string | null) =>
  !!message?.includes("Impossible de désactiver ce projet");

function ProjectDetails() {
  const param = useParams<"idProject">();
  const { idProject } = param;
  const { isAdmin, token } = useAuth();
  const queryClient = useQueryClient();
  const [openSnackbar] = useSnackbar(snackbarSuccessOptions);
  const [openSnackbarError] = useSnackbar(snackbarErrorOptions);
  const clasx = useStyles();
  const navigate = useNavigate();
  const { control, setValue } = useForm({
    mode: "onChange",
  });

  const [project, setProject] = useState<ProjectType | null>(null);
  const [active, setActive] = useState<boolean>(true);
  const [modalDeleteOpen, setModalDeleteOpen] = useState<boolean>(false);
  const [showImportModal, setShowImportModal] = useState<boolean>(false);
  const [showImportEditModal, setShowImportEditModal] =
    useState<boolean>(false);
  const [modalActive, setModalActive] = useState<boolean>(false);
  const [isAlias, setIsAlias] = useState<boolean>(false);
  const [cleanEmail, setCleanEmail] = useState<string>("");
  const [contact, setContact] = useState<any>(null);
  const [errorUpload, setErrorUpload] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [formValues, setFormValues] = useState<any>({
    medias: (project?.medias || []).map((media: any) => media || {}),
  });

  const canEdit = isAdmin();
  const isProjectDisabled = project?.isDisable || project?.companyDisable;

  const fetchProject = useCallback(async () => {
    ProjectServices.GetProject(token, idProject || "").then((pr: any) => {
      setProject(pr);
      setActive(!pr?.isDisable && !pr?.companyDisable);
      setContact(pr?.contact);

      let result = false;
      const email = (pr?.contact as any)?.email;
      const regex = /\+\d{13}@/;
      if (email) {
        result = regex.test(email);
      }
      if (result) {
        const modifiedEmail = email.replace(regex, "@");
        setCleanEmail(modifiedEmail);
        setIsAlias(true);
      } else {
        setIsAlias(false);
      }
    });
  }, [idProject, setActive, token]);

  const {
    openPopin: openPopinDockyard,
    togglePopin: toggleDock,
    handleSubmit: handleSubmitDock,
    isOpened: isDockyardPopinOpened,
    error: errorDockyard,
    submitting: submittingDockyard,
    dockyard: formDockyard,
  } = useDockyardForm(fetchProject);
  const {
    togglePopin,
    openPopin: openPopinProject,
    isOpened,
    submitting,
    handleSubmit,
    error,
    project: formProject,
  } = useProjectForm(fetchProject);

  const handleSwitch = () => {
    setActive((prev) => !prev);
  };

  const onImportFinish = async () => {};

  const handleEditProject = async () => {
    if (project) {
      await openPopinProject(project._id);
    }
  };

  const handleSwitchDock = (d: IDockyard) => {
    if (d && d._id && localStorage) {
      localStorage.setItem(d._id, JSON.stringify(d));
      navigate(`/projets/${idProject}/${d._id}`);
    }
  };

  const handleSwitchToggle = async () => {
    setModalActive(true);
  };

  const deleteProject = async () => {
    await ProjectServices.DeleteProject(token, project._id);
    navigate("/projets");
  };

  const disableProject = async () => {
    setIsLoading(true);
    try {
      const result = await ProjectServices.DisabledProject(
        token,
        idProject || ""
      );

      if (result?.error) {
        openSnackbarError(
          isProjectDeactivationBlockedError(result.error)
            ? PROJECT_DEACTIVATION_BLOCKED_MESSAGE
            : result.error
        );
        return;
      }

      setModalActive(false);
      await fetchProject();
      await queryClient.invalidateQueries({
        queryKey: [CacheInvalidationAction.GET_DOCKYARDS],
      });
      await queryClient.invalidateQueries({
        queryKey: [CacheInvalidationAction.GET_EDBS],
      });
      setActive(false);
      setErrorMessage(null);
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Une erreur est survenue lors de la désactivation du projet";
      openSnackbarError(
        isProjectDeactivationBlockedError(message)
          ? PROJECT_DEACTIVATION_BLOCKED_MESSAGE
          : message
      );
    } finally {
      setModalActive(false);
      setIsLoading(false);
    }
  };

  const enableProject = async () => {
    setIsLoading(true);
    try {
      await ProjectServices.EnableProject(token, idProject || "");
      setModalActive(false);
      await fetchProject();
      await queryClient.invalidateQueries({
        queryKey: [CacheInvalidationAction.GET_DOCKYARDS],
      });
      await queryClient.invalidateQueries({
        queryKey: [CacheInvalidationAction.GET_EDBS],
      });
      setActive(true);
      setErrorMessage(null);
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Une erreur est survenue lors de l'activation du projet";
      setErrorMessage(message);
      setModalActive(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseError = () => {
    setErrorMessage(null);
  };

  useEffect(() => {
    setFormValues({
      medias: (project?.medias || []).map((media: any) => media || {}),
    });
  }, [project]);

  useEffect(() => {
    setValue("medias", formValues.medias, { shouldValidate: false });
  }, [formValues.medias, setValue, formValues]);

  useEffect(() => {
    fetchProject();
  }, [fetchProject, idProject]);

  const filters = useMemo(
    () => ({
      projectId: idProject,
      companyDisable: false,
      ...(active
        ? { isActive: true, projectDisable: false }
        : { isActive: false }),
    }),
    [idProject, active]
  );

  const { data: dockyards } = useQuery({
    queryKey: [CacheInvalidationAction.GET_DOCKYARDS, filters],
    queryFn: () => DockyardServices.getDockyards(filters),
    enabled: !!idProject,
    staleTime: 1000 * 60,
  });

  const bodyTableDock =
    dockyards && dockyards?.data
      ? dockyards?.data
          .filter((d) =>
            active
              ? !d.isDisable && !d?.projectDisable && !d?.companyDisable
              : true
          )
          .map((d) => {
            const isDisabled =
              d.isDisable || d.projectDisable || d.companyDisable;
            return [
              {
                onClickAction: () => handleSwitchDock(d),
                value: (
                  <Text className={isDisabled ? clasx.DisabledText : ""}>
                    {d.code}
                  </Text>
                ),
              },
              {
                onClickAction: () => handleSwitchDock(d),
                value: (
                  <Text
                    className={isDisabled ? clasx.DisabledText : ""}
                    type="important"
                  >
                    {d?.name}
                  </Text>
                ),
              },
              {
                onClickAction: () => handleSwitchDock(d),
                value: (
                  <div role="button">
                    <ProjectStatusBadge
                      disabled={isDisabled}
                      status={d.status}
                    />
                  </div>
                ),
              },
            ];
          })
      : [];

  const renderHead = () => (
    <div
      style={{
        display: "flex",
        marginLeft: 18,
        justifyContent: "space-between",
        width: "100%",
      }}
    >
      <div className={clasx.customHeader}>
        <div className={clasx.calendar}>
          <CalendarCheckFillIcon className={clasx.calendarIcon} />
          Créé le{" "}
          {(project as any)?.createdAt &&
            dateformat(
              new Date((project as any)?.createdAt),
              DEFAULT_DATE_FORMAT
            )}
        </div>
        <div className={clasx.statusBadge}>
          <ProjectStatusBadge status={project?.status} />
        </div>
        <JTooltip
          title={
            project?.companyDisable
              ? "Le projet est inactif car l'entreprise associée est inactive"
              : ""
          }
        >
          <FormControlLabel
            className={clasx.switchButton}
            control={
              <Switch
                checked={!isProjectDisabled}
                onChange={handleSwitchToggle}
                name="activeCompany"
                color="primary"
                disabled={!canEdit || project?.companyDisable || isLoading}
              />
            }
            label="Projet actif"
          />
        </JTooltip>
      </div>
      <Tooltip
        title={
          dockyards?.data.length > 0
            ? "Vous ne pouvez pas supprimer ce projet car il a des chantiers liés"
            : ""
        }
      >
        <span>
          <Button
            disabled={
              !canEdit || dockyards?.data.length > 0 || isProjectDisabled
            }
            style={{ marginRight: 40, marginTop: 20 }}
            onClick={() => setModalDeleteOpen(true)}
            className={[
              clasx.buttonDelete,
              dockyards?.data.length > 0 || isProjectDisabled ? "disabled" : "",
            ].join(" ")}
          >
            Supprimer le projet
          </Button>
        </span>
      </Tooltip>
    </div>
  );

  if (!idProject) {
    return null;
  }
  const EditButton = (
    <JTooltip
      title={
        (isProjectDisabled && "Vous ne pouvez pas éditer un projet inactif.") ||
        (!canEdit && "Vous n'avez pas les droits pour éditer ce projet.")
      }
    >
      <UIButton
        className={clasx.editButton} // Add this class
        onClick={handleEditProject}
        disabled={isProjectDisabled || !canEdit}
      >
        <Edit2LineIcon />
      </UIButton>
    </JTooltip>
  );

  const email = contact?.email;
  const firstname = contact?.firstname ?? "";
  const lastname = contact?.lastname ?? "";
  const fullname =
    firstname.length > 0 && lastname.length > 0
      ? `${firstname} ${lastname.toUpperCase()}`
      : "N/A";

  return (
    <Layout
      className={clasx.ProjectDetailLayout}
      renderHead={renderHead}
      title={project?.name}
    >
      <div style={{ display: "flex", marginBottom: 18 }}>
        <InfoContainer
          className={[clasx.projectInformationHeader, clasx.infoHeight].join(
            " "
          )}
          leftHeader={
            <div className={clasx.infoHeader}>
              <FormatListBulleted />
              <Text type="h3">Informations projet</Text>
            </div>
          }
          rightHeader={EditButton}
        >
          <div className="about">
            <div className="left">
              <table>
                <tr>
                  <td className="label">Code projet</td>
                  <td className="data">{project?.code}</td>
                </tr>
                <tr>
                  <td className="label">Nom projet</td>
                  <td className="data">{project?.name}</td>
                </tr>
                {project?.description && (
                  <tr>
                    <td className="label">Description</td>
                    <td className="data">{project?.description}</td>
                  </tr>
                )}
              </table>
            </div>
          </div>
        </InfoContainer>
        <InfoContainer
          className={[clasx.projectInformationHeader, clasx.infoHeight].join(
            " "
          )}
          leftHeader={
            <div className={clasx.infoHeader}>
              <UserLineIcon />
              <Text type="h3">Informations contact</Text>
            </div>
          }
          rightHeader={EditButton}
        >
          <div className="about">
            <div className="left">
              <table>
                <tr>
                  <td className="label">Entreprise</td>
                  <td className="data">{(project?.company as any)?.name}</td>
                </tr>
                <tr>
                  <td className="label">Contact</td>
                  <td className={`data ${isAlias ? "disable" : ""}`}>
                    {fullname}
                  </td>
                </tr>
                <tr>
                  <td className="label">Email</td>
                  <td className={`data ${isAlias ? "disable" : ""}`}>
                    {cleanEmail || (email ?? "N/A")}
                  </td>
                </tr>
                {(project?.contact as any)?.phone && (
                  <tr>
                    <td className="label">Téléphone</td>
                    <td className={`data ${isAlias ? "disable" : ""}`}>
                      {(project?.contact as any)?.phone}
                    </td>
                  </tr>
                )}
              </table>
            </div>
          </div>
        </InfoContainer>
      </div>
      <InfoContainer
        leftHeader={
          <div className={clasx.infoHeader}>
            <DockyardIcon />
            <Text type="h3">Chantier(s)</Text>
          </div>
        }
        rightHeader={
          <div style={{ position: "relative", display: "flex" }}>
            <FormControlLabel
              className={clasx.SwitchControl}
              label="Chantiers actifs"
              control={
                <Switch
                  checked={active}
                  onChange={handleSwitch}
                  name="showCompany"
                  color="primary"
                />
              }
            />
            <JTooltip
              title={
                isProjectDisabled
                  ? "Vous ne pouvez pas ajouter de chantier à un projet inactif"
                  : ""
              }
            >
              <span>
                <Button
                  className={[
                    isProjectDisabled || project?.status === ProjectStatus.DONE
                      ? clasx.ctaDone
                      : "",
                  ].join(" ")}
                  variant="outlined"
                  onClick={() => openPopinDockyard()}
                  disabled={!canEdit || isProjectDisabled}
                >
                  <Addicon />
                  Ajouter un chantier
                </Button>
              </span>
            </JTooltip>
            <BtnPoper
              BtnLabel="Opérations en masse"
              BtnComponent={Button}
              BtnProps={{
                variant: "outlined",
                disabled: isProjectDisabled || !canEdit,
              }}
              BtnIcon={ExpandMore}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  minWidth: "300px",
                }}
              >
                <Button
                  onClick={() => setShowImportModal(true)}
                  variant="text"
                  disabled={isProjectDisabled}
                  sx={{
                    justifyContent: "flex-start",
                    textAlign: "left",
                    borderRadius: 0,
                    color: "black !important",
                    backgroundColor: "transparent !important",
                    border: "none !important",
                    padding: "6px",
                    minWidth: "300px",
                    "&:hover": {
                      backgroundColor: "#e0e0e0 !important",
                      color: "black !important",
                      border: "none !important",
                    },
                    "&.MuiButton-text:hover": {
                      backgroundColor: "#e0e0e0 !important",
                    },
                    "&.MuiButton-root:hover": {
                      backgroundColor: "#e0e0e0 !important",
                    },
                    "& svg": {
                      color: "black !important",
                      marginRight: "8px",
                    },
                    "& .MuiButton-label": {
                      color: "black !important",
                    },
                    "& span": {
                      color: "black !important",
                    },
                  }}
                >
                  <Download2LineIcon />
                  Importer des chantiers
                </Button>
                <Button
                  onClick={() => setShowImportEditModal(true)}
                  variant="text"
                  disabled={isProjectDisabled}
                  sx={{
                    justifyContent: "flex-start",
                    textAlign: "left",
                    borderRadius: 0,
                    color: "black !important",
                    backgroundColor: "transparent !important",
                    border: "none !important",
                    padding: "6px",
                    minWidth: "300px",
                    "&:hover": {
                      backgroundColor: "#e0e0e0 !important",
                      color: "black !important",
                      border: "none !important",
                    },
                    "&.MuiButton-text:hover": {
                      backgroundColor: "#e0e0e0 !important",
                    },
                    "&.MuiButton-root:hover": {
                      backgroundColor: "#e0e0e0 !important",
                    },
                    "& svg": {
                      color: "black !important",
                      marginRight: "8px",
                    },
                    "& .MuiButton-label": {
                      color: "black !important",
                    },
                    "& span": {
                      color: "black !important",
                    },
                  }}
                >
                  <Edit2LineIcon />
                  Mettre à jour des chantiers
                </Button>
              </Box>
            </BtnPoper>
          </div>
        }
      >
        <Table
          bodyData={bodyTableDock}
          rowsPerPage={50}
          thead={tHead}
          bodyMissingLabelText={
            !bodyTableDock.length
              ? "Aucun chantier n'a encore été ajouté à ce projet"
              : ""
          }
          className={clasx.noPadding}
          usePaper={false}
        />
      </InfoContainer>

      <InfoContainer
        leftHeader={
          <div className={clasx.infoHeader}>
            <AttachFileIcon />
            <Text type="h3">Pièces jointes</Text>
          </div>
        }
        className={clasx.attachements}
      >
        {project?.medias?.length > 0 && (
          <div>
            <Text type="h3">Liste des médias</Text>
          </div>
        )}
        <form>
          <Controller
            control={control}
            name="medias"
            render={({ field: { onChange, value } }) => (
              <FileUploader
                value={value}
                onChange={(values) => {
                  onChange(values);
                }}
                isAdmin={isAdmin()}
                type="project"
                company={(project?.company as any)?._id}
                id={project?._id ?? undefined}
                isEdit={false}
                isLargeVersion
                isReadOnly={isProjectDisabled || !canEdit}
              />
            )}
          />
        </form>
      </InfoContainer>
      <JImportModal
        dataName="chantiers"
        showImportModal={showImportModal}
        contextId={idProject}
        setShowImportModal={setShowImportModal}
        type={ImportType.DOCKYARD_SIMPLE}
        onImportFinish={onImportFinish}
        openSnackbar={openSnackbar}
      />
      <JImportModal
        contextId={idProject}
        showImportModal={showImportEditModal}
        setShowImportModal={setShowImportEditModal}
        type={ImportType.DOCKYARD_SIMPLE_UPDATE}
        onImportFinish={onImportFinish}
        openSnackbar={openSnackbar}
        canExportData
        filters={filters}
        dataName="chantiers"
      />
      <Modal
        open={modalActive}
        onCancel={() => setModalActive(false)}
        className={clasx.modalDesactive}
        title="Attention"
        textValidate={isProjectDisabled ? "Activer" : "Désactiver"}
        onConfirm={isProjectDisabled ? enableProject : disableProject}
      >
        <div className={clasx.modalContainer}>
          {isProjectDisabled
            ? "Vous êtes sur le point de réactiver ce projet, êtes-vous sûr de vouloir continuer ?"
            : "Vous êtes sur le point de désactiver ce projet, êtes-vous sûr de vouloir continuer ?"}
        </div>
      </Modal>
      <Modal
        open={errorUpload !== ""}
        onCancel={() => {
          setErrorUpload("");
        }}
        className={clasx.modalDesactive}
        title="Attention"
        textValidate="Réessayer"
        onConfirm={() => {
          setErrorUpload("");
        }}
      >
        <div className={clasx.modalContainer}>{errorUpload}</div>
      </Modal>
      <Popin
        className={clasx.ProjectsPage_Popin}
        openPopin={isOpened}
        toggleDrawer={togglePopin}
      >
        <ProjectForm
          project={formProject}
          onSubmit={handleSubmit}
          projectError={error}
          onReset={() => togglePopin(false)}
          isSubmitting={submitting}
        />
      </Popin>
      <Popin
        className={clasx.ProjectsPage_Popin}
        openPopin={isDockyardPopinOpened}
        toggleDrawer={toggleDock}
      >
        <DockyardForm
          dockyard={formDockyard}
          defaultValuesForm={{
            project: project?._id,
            company: project?.company?._id,
          }}
          onSubmit={handleSubmitDock}
          onCancel={() => toggleDock(false)}
          error={errorDockyard}
          isSubmitting={submittingDockyard}
        />
      </Popin>
      <Modal
        open={modalDeleteOpen}
        onCancel={() => setModalDeleteOpen(false)}
        onValidate={async () => {
          setModalDeleteOpen(false);
          await deleteProject();
        }}
        textCancel="Annuler"
        textValidate="Supprimer"
        title="Confirmation de suppression"
      >
        <div style={{ textAlign: "center" }}>
          <Text>
            Vous êtes sur le point de supprimer définitivement ce projet.
            <br />
            Êtes-vous sûr de vouloir continuer ?
          </Text>
        </div>
      </Modal>
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseError}
          severity="error"
          sx={{ width: "100%", fontSize: "14px" }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Layout>
  );
}

export default ProjectDetails;

const tHead = [
  { id: "dockyardCode", label: "code chantier" },
  { id: "chantier", label: "Nom chantier" },
  { id: "statut", label: "statut" },
];

const useStyles = createUseStyles(() => ({
  editButton: {
    color: "rgba(0, 0, 0) !important",
    "&:hover": {
      color: "rgba(0, 0, 0) !important",
      backgroundColor: "rgba(0, 0, 0, 0.04) !important",
    },
    "&:disabled": {
      color: "rgba(0, 0, 0, 0.26) !important",
    },
    "& svg": {
      color: "inherit !important",
    },
  },
  buttonDelete: {
    "&.disabled": {
      pointerEvents: "none !important",
      background: "#ccc",
      cursor: "unset",
      "&:hover, &:focus, &:active": {
        background: "#ccc",
      },
    },
    marginRight: 40,
  },
  ProjectDetailLayout: {
    "& .MuiIconButton-label": {
      transform: "scale(0.6) !important",
    },
  },
  attachements: {
    marginTop: 10,
    marginBottom: 20,
  },
  infoHeader: {
    display: "flex",
    alignItems: "center",
    "& h3": {
      margin: "0 8px",
    },
  },
  infoHeight: {
    minHeight: "295px",
    '& div[class="bodyContainer*"]': {
      minHeight: "335px",
    },
  },
  customHeader: {
    display: "flex",
    marginLeft: 18,
    alignItems: "center",
  },
  calendar: {
    display: "flex",
    padding: "0 18px",
    alignItems: "center",
    borderLeft: `1px solid ${theme.color.gray2}`,
    borderRight: `1px solid ${theme.color.gray2}`,
    color: theme.color.gray5,
    fontSize: 14,
    marginRight: 18,
  },
  calendarIcon: {
    width: 18,
    marginRight: 8,
  },
  statusBadge: {
    borderRight: `1px solid ${theme.color.gray2}`,
    paddingRight: "18px",
    marginRight: "18px",
  },
  switchButton: {
    "& span": {
      fontSize: 14,
    },
  },
  projectInformationHeader: {
    "&:first-child": {
      marginRight: 18,
    },
    width: "68%",
    "& .about": {
      display: "flex",
      "& .left, & .right": {
        flex: 1,
      },
      "& .left": {
        paddingRight: "10px",
      },
      "& .right": {
        marginLeft: "10px",
      },
      "& table": {
        width: "100%",
        borderSpacing: "0 20px",
      },
      "& .label": {
        color: theme.color.black,
        width: "50%",
        verticalAlign: "top",
      },
      "& .data": {
        color: theme.color.blue,
        fontWeight: "bold",
        width: "50%",
        verticalAlign: "top",
      },
      "& .disable": {
        color: theme.color.gray5,
      },
    },
  },
  modalDesactive: {
    display: "flex",
    padding: theme.spacing(1),
    alignItems: "center",
    justifyContent: "center",
    "& .MuiDialog-paperWidthSm": {
      maxWidth: "508px !important",
    },
    "& .MuiDialogActions-spacing": {
      justifyContent: "space-between !important",
    },
  },
  modalContainer: {
    padding: "0 50px",
    textAlign: "center",
  },
  BtnOnPopper: {
    "&.MuiButtonGroup-grouped": {
      padding: 6,
      justifyContent: "flex-start",
      minWidth: "300px",
      background: theme.color.white,
    },
  },
  ctaDone: {
    color: `${theme.color.gray3} !important`,
    pointerEvents: "none",
    "&:hover": {
      color: `${theme.color.gray3} !important`,
    },
  },
  noPadding: {
    padding: 0,
  },
  DisabledText: {
    color: `${theme.color.gray3} !important`,
  },
  SwitchControl: {
    "& .MuiTypography-body1": {
      fontSize: "14px",
    },
  },
  ProjectsPage__AddCta: {
    marginRight: "10px !important",
  },
  ProjectsPage_Popin: {
    "& .MuiDrawer-paper": {
      width: "786px !important",
      overflowY: "unset !important",
    },
  },
}));
